import {
  LineChart,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import type { CashflowSummary, Currency } from "../../types";
import { formatK } from "../cashflow/CashflowContext";
import { formatYmMonthShort } from "../../utils/formatYmMonthShort";
import { LineChartEmptyCard } from "../datamintEmptyStates";

const PALETTE: Record<string, string> = {
  USD: "#22c55e",
  ILS: "#6366f1",
  EUR: "#f97316",
};

function mergeEndingBalances(
  summaries: Partial<Record<Currency | "EUR", CashflowSummary | undefined>>,
): { month: string; [key: string]: number | string }[] {
  const monthSet = new Set<string>();
  for (const s of Object.values(summaries)) {
    if (!s?.months) continue;
    for (const m of s.months) monthSet.add(m);
  }
  const months = [...monthSet].sort();
  return months.map((m) => {
    const row: { month: string; [key: string]: number | string } = {
      month: formatYmMonthShort(m),
    };
    for (const [ccy, summ] of Object.entries(summaries)) {
      if (!summ?.endingBalances) continue;
      const v = summ.endingBalances[m];
      if (typeof v === "number") row[ccy] = v;
    }
    return row;
  });
}

type Props = {
  usdSummary: CashflowSummary | undefined;
  ilsSummary: CashflowSummary | undefined;
  eurSummary: CashflowSummary | undefined;
  displayCurrency: Currency;
  isLoading: boolean;
};

export function DashboardLiquidityPanel({
  usdSummary,
  ilsSummary,
  eurSummary,
  displayCurrency,
  isLoading,
}: Props) {
  const keys = (
    [
      ["USD", usdSummary],
      ["ILS", ilsSummary],
      ["EUR", eurSummary],
    ] as const
  ).filter(([, s]) => s && (s.months?.length ?? 0) > 0);

  const data = mergeEndingBalances({
    USD: usdSummary,
    ILS: ilsSummary,
    EUR: eurSummary,
  });

  if (isLoading) {
    return (
      <div className="flex h-[400px] flex-col rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-4 h-9 animate-pulse rounded bg-slate-100" />
        <div className="flex-1 animate-pulse rounded-lg bg-slate-100" />
      </div>
    );
  }

  if (keys.length === 0 || data.length === 0) {
    return (
      <LineChartEmptyCard
        title="Bank Balances"
        headerLinkLabel="Manage Accounts"
        headerLinkTo="/files-integrations"
        ctaTo="/files-integrations"
      />
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-base font-semibold text-slate-900">
        Bank Balances
      </h3>
      <div className="h-[300px] w-full min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <YAxis
              tickFormatter={(v) => formatK(Number(v), displayCurrency)}
              tick={{ fontSize: 11 }}
              stroke="#94a3b8"
            />
            <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" />
            <Tooltip
              formatter={(value: number | string, name: string) => [
                formatK(Number(value), displayCurrency),
                name,
              ]}
              contentStyle={{
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                fontSize: "12px",
              }}
            />
            {keys.map(([ccy]) => (
              <Line
                key={ccy}
                type="monotone"
                dataKey={ccy}
                name={ccy}
                stroke={PALETTE[ccy] ?? "#64748b"}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap justify-center gap-x-6 gap-y-2 border-t border-slate-100 pt-3 text-xs">
        {keys.map(([ccy]) => (
          <span
            key={ccy}
            className="inline-flex items-center gap-2 text-slate-700"
          >
            <span
              className="h-3.5 w-3.5 rounded-full"
              style={{ backgroundColor: PALETTE[ccy] }}
            />
            {ccy}
          </span>
        ))}
      </div>
    </div>
  );
}
